import {
  AiStrategy,
  QuickWinHandler,
  QuickWinStrategy,
} from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import { QUERY_QUICK_WIN_STRATEGIES } from "./constants";
import { useLazyQuery } from "@apollo/client";

export type LlmQuickWinsContextType = {
  strategies: QuickWinStrategy[];
  getStrategy: (strategyId: string) => QuickWinStrategy | undefined;
  strategyEnabled(strategyCode: string): boolean;
  refreshStrategies: () => Promise<QuickWinStrategy[]>;
};

export const LlmQuickWinsContext = React.createContext<
  LlmQuickWinsContextType | undefined
>(undefined);

export const LlmQuickWinsProvider = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const [strategies, setStrategies] = React.useState<QuickWinStrategy[]>([]);

  const [queryQuickWinStrategies] = useLazyQuery<{
    quickWinStrategies: QuickWinStrategy[];
  }>(QUERY_QUICK_WIN_STRATEGIES);

  React.useEffect(() => {
    (async () => {
      refresh();
    })();
  }, []);

  const refresh = async () => {
    try {
      const result = await queryQuickWinStrategies();
      if (result.data?.quickWinStrategies) {
        setStrategies(result.data.quickWinStrategies);
        return result.data.quickWinStrategies;
      }
      return strategies;
    } catch (e) {
      return strategies;
    }
  };

  const getStrategy = (code: string) => {
    const strategy = strategies.find((s) => s.config.code === code);
    if (!strategy) return undefined;
    return strategy;
  };

  const strategyEnabled = (strategyCode: string) => {
    const strategy = getStrategy(strategyCode);
    if (!strategy) return false;
    return strategy ? strategy.enabled : true; // default to enabled
  };

  return (
    <LlmQuickWinsContext.Provider
      value={{
        strategies,
        getStrategy: getStrategy,
        strategyEnabled: strategyEnabled,
        refreshStrategies: refresh,
      }}
    >
      {children}
    </LlmQuickWinsContext.Provider>
  );
};
